import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { HOST_API } from 'src/config-global';
import { useSnackbar } from 'src/components/snackbar';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import ChatNotificationToast from 'src/sections/chat/chat-notification-toast';
import {
  onNewMessage,
  onMessageUpdated,
  onConversationUpdated,
  onConversationNew,
  onTypingStart,
  onTypingStop,
  onUpdateUserStatus,
  onReadReceipt,
  onCallRequest,
  onCallAccept,
  onCallReject,
  onCallHangUp,
  onGroupCallRequest,
  onGroupCallEnd,
  onGroupCallActive,
  onGroupCallInactive,
  onCallParticipantsUpdate,
  setActiveConversation,
} from '../store/slices/chatSlice';

// ─── useChatSocket ─────────────────────────────────────────────────────────────

// Singleton socket instance to persist across route changes
let socket = null;

/**
 * Connects to Socket.IO and dispatches incoming events to the Redux store.
 * Also exposes emit helpers for typing indicator.
 *
 * Usage: 
 * 1. Call in DashboardLayout to maintain global connection and listeners.
 * 2. Call in Chat components to access emit helpers.
 */
export function useChatSocket() {
  const dispatch = useDispatch();
  const router = useRouter();
  const { user } = useSelector(state => state.auth);
  const { conversations, activeConversationId } = useSelector(state => state.chat);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const stateRef = useRef({ conversations, activeConversationId, user });
  const socketRef = useRef(null);

  useEffect(() => {
    stateRef.current = { conversations, activeConversationId, user };
  }, [conversations, activeConversationId, user]);

  useEffect(() => {
    if (!user?.id) {
      if (socket) {
        console.log('🔌 Disconnecting chat socket on logout');
        socket.disconnect();
        socket = null;
      }
      return undefined;
    }

    if (!socket) {
      console.log('🔌 Initializing singleton chat socket for user:', user.id);
      const accessToken = localStorage.getItem('accessToken');
      socket = io(HOST_API, {
        auth: {
          token: accessToken,               // JWT for server-side verification
          userId: String(user.id || user._id), // kept as fallback / legacy
          orgId: user.organizationId,
        },
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socket.on('connect', () => {
        console.log('✅ Chat socket connected:', socket.id);
      });

      socket.on('disconnect', (reason) => {
        console.warn('❌ Chat socket disconnected:', reason);
      });
    }

    socketRef.current = socket;
    
    // ── Incoming events (Always re-attach with latest dispatch) ──────────────────
    console.log('👂 Attaching global chat listeners...');

    socket.off('message:new');
    socket.on('message:new', (message) => {
        console.log('📩 Socket onNewMessage:', message.body);
        dispatch(onNewMessage(message));
        
        const { user: latestUser, conversations: latestConversations, activeConversationId: latestActiveId } = stateRef.current;

        // Emit delivery receipt if message is not from me
        const myId = String(latestUser?.id || latestUser?._id);
        const senderId = String(message.sender?._id || message.sender);
        if (senderId !== myId) {
            socket.emit('message:delivered', { 
                messageId: message._id, 
                conversationId: message.conversationId 
            });

            // Trigger background toast notifications
            const cid = String(message.conversationId);
            const conv = latestConversations?.byId?.[cid];
            let isMuted = false;
            let senderName = '';
            let avatarUrl = null;
            
            if (conv) {
                const myParticipant = conv.participants?.find((p) => p.isCurrentUser);
                if (myParticipant?.mutedUntil) {
                    isMuted = new Date(myParticipant.mutedUntil) > new Date();
                }
                
                // Find sender details from participants list
                const sender = conv.participants?.find((p) => {
                    const pId = p.userId?._id || p.userId;
                    const pAdminRef = p.userId?.adminRef;
                    return (pId?.toString() === senderId) || (pAdminRef?.toString() === senderId);
                });
                if (sender) {
                    senderName = sender.name || `${sender.firstName || ''} ${sender.lastName || ''}`.trim();
                    avatarUrl = sender.avatarUrl;
                    if (!avatarUrl && sender.documents?.photo) {
                        const rawPhoto = Array.isArray(sender.documents.photo) ? sender.documents.photo[0] : sender.documents.photo;
                        if (rawPhoto) {
                            avatarUrl = rawPhoto.startsWith('http') ? rawPhoto : `${HOST_API}/${rawPhoto}`;
                        }
                    }
                }
            }
            
            if (typeof message.sender === 'object') {
                if (!senderName) {
                    senderName = `${message.sender.firstName || ''} ${message.sender.lastName || ''}`.trim() || message.sender.name;
                }
                if (!avatarUrl) {
                    const rawPhoto = message.sender.avatarUrl || (Array.isArray(message.sender.documents?.photo) ? message.sender.documents.photo[0] : message.sender.documents?.photo);
                    if (rawPhoto) {
                        avatarUrl = rawPhoto.startsWith('http') ? rawPhoto : `${HOST_API}/${rawPhoto}`;
                    }
                }
            }
            if (!senderName) {
                senderName = 'Unknown User';
            }

            const isChatActive = window.location.pathname.includes('/chat') || window.location.pathname.includes('/dashboard/chat');
            const isCurrentChat = isChatActive && cid === String(latestActiveId);
            
            if (!isCurrentChat && !isMuted) {
                enqueueSnackbar(message.body || '', {
                    anchorOrigin: { vertical: 'top', horizontal: 'left' },
                    content: (key) => (
                        <ChatNotificationToast
                            snackbarId={key}
                            message={message}
                            senderName={senderName}
                            avatarUrl={avatarUrl}
                            conversationName={conv?.type === 'group' ? conv.name : undefined}
                            conversationId={cid}
                        />
                    ),
                });
            }
        }
    });

    socket.off('message:updated');
    socket.on('message:updated', (message) => {
        dispatch(onMessageUpdated(message));
    });

    socket.off('conversation:updated');
    socket.on('conversation:updated', (conv) => {
        dispatch(onConversationUpdated(conv));
    });

    socket.off('conversation:new');
    socket.on('conversation:new', (conv) => {
        dispatch(onConversationNew(conv));
    });

    socket.off('typing:start');
    socket.on('typing:start', ({ conversationId, userId }) => {
        dispatch(onTypingStart({ conversationId, userId }));
    });

    socket.off('typing:stop');
    socket.on('typing:stop', ({ conversationId, userId }) => {
        dispatch(onTypingStop({ conversationId, userId }));
    });

    socket.off('user:status');
    socket.on('user:status', ({ userId, status }) => {
        dispatch(onUpdateUserStatus({ userId, status }));
    });

    socket.off('messages:read');
    socket.on('messages:read', (data) => {
        dispatch(onReadReceipt(data));
    });

    // ── WebRTC Calling listeners ───────────────────────────────────────
    socket.off('call:request');
    socket.on('call:request', (data) => {
        const myId = String(user.id || user._id);
        if (data.from !== myId) {
            dispatch(onCallRequest(data));
        }
    });

    socket.off('call:accept');
    socket.on('call:accept', (data) => {
        dispatch(onCallAccept(data));
    });

    socket.off('call:reject');
    socket.on('call:reject', (data) => {
        dispatch(onCallReject(data));
    });

    socket.off('call:hang-up');
    socket.on('call:hang-up', (data) => {
        dispatch(onCallHangUp(data));
    });

    socket.off('call:group-start');
    socket.on('call:group-start', (data) => {
        const myId = String(user.id || user._id);
        if (data.from !== myId) {
            // Show ringing UI for other participants
            dispatch(onGroupCallRequest(data));
        }
        
        // Always persist the active call so users can join late from the chat banner,
        // even the caller should have this record (though they are already in the call).
        dispatch(onGroupCallActive({
            conversationId: data.conversationId,
            callerId: data.from,
            type: data.type,
        }));
    });

    socket.off('call:group-end');
    socket.on('call:group-end', (data) => {
        dispatch(onGroupCallEnd(data));
        dispatch(onGroupCallInactive({ conversationId: data.conversationId }));
    });

    socket.off('call:participants-update');
    socket.on('call:participants-update', (data) => {
        dispatch(onCallParticipantsUpdate(data));
    });

    socket.off('call:ice-candidate');
    // call:ice-candidate is no longer used (both call types use LiveKit)

    return () => {
      // Clear local ref but keep global socket alive
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /** Join a conversation room (call after opening a conversation) */
  const joinConversation = useCallback((conversationId) => {
    if (socket && conversationId) {
      socket.emit('join:conversation', conversationId);
    }
  }, []);

  /** Leave a conversation room */
  const leaveConversation = useCallback((conversationId) => {
    if (socket && conversationId) {
      socket.emit('leave:conversation', conversationId);
    }
  }, []);

  /** Emit typing:start event */
  const emitTypingStart = useCallback((conversationId) => {
    if (socket && conversationId) {
      socket.emit('typing:start', { conversationId });
    }
  }, []);

  /** Emit typing:stop event */
  const emitTypingStop = useCallback((conversationId) => {
    if (socket && conversationId) {
      socket.emit('typing:stop', { conversationId });
    }
  }, []);

  return {
    socket: socketRef,
    joinConversation,
    leaveConversation,
    emitTypingStart,
    emitTypingStop,
    emitCallEvent: useCallback((event, data) => {
        if (socket) socket.emit(event, data);
    }, []),
  };
}

export default useChatSocket;

// ---------------------------------------------------------------------------
// Lightweight hook for components that only need to EMIT events.
// Does NOT re-attach socket listeners, so it is safe to call from any
// component without causing duplicate listener registrations.
// ---------------------------------------------------------------------------
export function useChatEmitter() {
  return {
    emitCallEvent: useCallback((event, data) => {
      if (socket) socket.emit(event, data);
    }, []),
    joinConversation: useCallback((conversationId) => {
      if (socket && conversationId) socket.emit('join:conversation', conversationId);
    }, []),
    leaveConversation: useCallback((conversationId) => {
      if (socket && conversationId) socket.emit('leave:conversation', conversationId);
    }, []),
    emitTypingStart: useCallback((conversationId) => {
      if (socket && conversationId) socket.emit('typing:start', { conversationId });
    }, []),
    emitTypingStop: useCallback((conversationId) => {
      if (socket && conversationId) socket.emit('typing:stop', { conversationId });
    }, []),
  };
}
